import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, Switch, FormControlLabel, Alert, Snackbar,
  CircularProgress, Grid, Card, CardContent, Tooltip, Autocomplete, Checkbox,
  ListSubheader
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
// Removed PrimeReact imports - using Material-UI instead
import axios from '../../utils/CustomAxios';
import useStatusToggle from '../../hooks/useStatusToggle';
import StatusSwitch from '../../sub_components/status_switch';
import { useDispatch, useSelector } from 'react-redux';
import { updatepageName } from '../../Redux/page';
import {
  DURATION_UNITS,
  formatDateTime,
  isCustomWindowPast,
  planDurationLabel,
  planDurationSummary,
  planExpiryFrom,
} from '../../utils/planDuration';
import { useGetCategories, useGetInventory, useGetProducts } from '../globalFunctions';
import { SubscriptionPurchase } from './subscription_purchase';
import {
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  growthDialogPaperSx,
  growthDialogTitleSx,
  growthDialogActionsSx,
  GROWTH_FONT,
} from '../growth_hub/GrowthModal';

const createDefaultFormData = () => ({
  plan_name: '',
  plan_type: 'premium',
  description: '',
  duration_value: 12,
  duration_type: 'months',
  custom_start_date: '',
  custom_end_date: '',
  base_price: '',
  currency: 'INR',
  plan_scope: 'specific_hubs',
  applicable_hubs: [],
  single_hub: null,
  show_exclusive_offers_only: false,
  mapped_offers: [],
  additional_benefits: [],
  loyalty_multiplier: '2.00',
  loyalty_once_percentage: '0.50',
  random_offer_trigger_orders: 0,
  random_offer_percentage: '0.00',
  smart_deal_product_ids: [],
  smart_deal_non_subscribed_product_ids: [],
  smart_deal_non_subscribed_limit: 0,
  smart_deal_show_to_non_subscribers: false,
  is_active: true,
  sort_order: 0
});

const formatBenefitLabel = (code) =>
  String(code || '')
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const formatDateTimeLocalValue = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (num) => String(num).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export function SubscriptionPlans() {
  const dispatch = useDispatch();
  const inventory = useSelector(state => state.inventory.inventory) || [];
  const categories = useSelector(state => state.category.category) || [];
  const offers = useSelector(state => state.offers.offers) || [];
  const products = useSelector(state => state.products.products) || [];
  const getInventory = useGetInventory();
  const getCategories = useGetCategories();
  const getProducts = useGetProducts();

  // Filter inventory based on user role and hub assignments (like product form)
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [plans, setPlans] = useState([]);
  const statusToggle = useStatusToggle();
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [benefitDialogOpen, setBenefitDialogOpen] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [benefitForm, setBenefitForm] = useState({
    code: '',
    name: '',
    description: '',
    config_mode: 'none',
    config_value: '',
    default_offer_ids: [],
    is_active: true
  });
  const [benefitFormErrors, setBenefitFormErrors] = useState({});
  const plansFetchedRef = useRef(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plan: null });
  const [purchaseDialog, setPurchaseDialog] = useState({ open: false, planId: null });
  const [formData, setFormData] = useState(createDefaultFormData());

  /*
   * "Members get 10 minutes, expiring at 5:35 pm."
   *
   * Computed from the same arithmetic the backend uses -- 30 days to a month,
   * 365 to a year -- so the form cannot describe an expiry the server will not
   * produce. Recomputed as the fields change rather than on save, because the
   * point is to see it before committing to it.
   */
  const durationPreview = useMemo(() => {
    if (formData.duration_type === 'custom') return null;
    const label = planDurationLabel(formData);
    if (!label) return null;
    return { label, expiry: formatDateTime(planExpiryFrom(formData)) };
  }, [formData.duration_type, formData.duration_value]);
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const smartDealOptions = useMemo(() => (
    (products || []).filter((product) => {
      const category = String(product?.item_category || '').toLowerCase();
      const isSmartDealCategory = Boolean(
        product?.bogo === true ||
        product?.bogo_offer === true ||
        category === 'bogo' ||
        category.includes('smart deal') ||
        category.includes('2 for 1') ||
        category.includes('2-for-1')
      );
      if (!isSmartDealCategory) return false;
      // Prefer products that already have hub inventory / batch stock
      const units = Number(
        product?.available_units ??
        product?.inventory_details?.available_units ??
        product?.capacities?.[0]?.available_units ??
        NaN
      );
      // Keep in list even if units unknown (legacy product payload), but flag later
      return true;
    }).map((product) => {
      const units = Number(
        product?.available_units ??
        product?.inventory_details?.available_units ??
        NaN
      );
      const hasStock = Number.isFinite(units) ? units > 0 : null;
      const baseName = product?.name || product?.item_name || `Product ${product?.value || product?.id}`;
      let suffix = '';
      if (hasStock === false) suffix = ' (out of stock — add batch)';
      else if (hasStock === null && !(product?.bogo_offer || product?.bogo)) {
        suffix = ' (verify hub batch)';
      }
      return {
        ...product,
        _smartDealLabel: `${baseName}${suffix}`,
        _hasSmartDealStock: hasStock,
      };
    })
  ), [products]);

  /**
   * Options for a smart-deal picker, INCLUDING anything already saved on the
   * plan that would not otherwise qualify.
   *
   * The pickers used to compute their value as
   * `smartDealOptions.filter(p => selectedIds.includes(p.id))`. A product saved
   * on the plan but outside a 2-for-1 category is not in smartDealOptions, so
   * it disappeared from the field while remaining on the plan — the screen
   * showed one chip while the plan held three ids, and there was no way to
   * remove them. You cannot fix what the form will not show you.
   *
   * Invalid entries are surfaced, labelled with the category that disqualifies
   * them, so they can be removed.
   */
  const optionsIncludingSaved = useCallback((selectedIds) => {
    const ids = Array.isArray(selectedIds) ? selectedIds.filter((id) => id != null) : [];
    const known = new Set(smartDealOptions.map((p) => p.value || p.id));
    const orphans = ids
      .filter((id) => !known.has(id))
      .map((id) => {
        const full = (products || []).find((p) => (p.value || p.id) === id);
        const name = full?.name || full?.item_name || `Product ${id}`;
        const category = full?.item_category || 'unknown category';
        return {
          ...(full || { id }),
          value: id,
          id,
          _smartDealLabel: `${name} — not a 2-for-1 category (${category})`,
          _hasSmartDealStock: null,
          _isOrphan: true,
        };
      });
    return [...smartDealOptions, ...orphans];
  }, [smartDealOptions, products]);

  /** Warn about saved selections customers will never actually see. */
  const orphanWarning = useCallback((selectedIds, label) => {
    const opts = optionsIncludingSaved(selectedIds);
    const orphans = opts.filter((o) => o._isOrphan);
    if (!orphans.length) return null;
    return `${label}: ${orphans.length} product(s) are not in a 2-for-1 category, so they will NOT appear on mobile. Move them to a smart-deal category or remove them.`;
  }, [optionsIncludingSaved]);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, []);

  // Note: Applicable hubs now work exactly like inventory selection in product_details.js
  // to provide intelligent switching based on user selections

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const inventoryId = inventory && inventory.length > 0 ? inventory[0].id : null;

      const plansResponse = await axios.get('subscriptions/plans/', {
        params: inventoryId ? { inventory_id: inventoryId } : {}
      });
      setPlans(plansResponse.data);
    } catch (error) {
      showSnackbar('Failed to fetch subscription plans. Please check your connection.', 'error');
    }
    setLoading(false);
  }, [inventory, showSnackbar]);

  const fetchBenefits = useCallback(async () => {
    try {
      const response = await axios.get('subscriptions/benefits/');
      setBenefits(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showSnackbar('Failed to fetch benefits', 'error');
    }
  }, [showSnackbar]);

  // Set page name on mount
  useEffect(() => {
    dispatch(updatepageName("subscription_plans"));
  }, [dispatch]);

  // Clear form errors when opening edit dialog
  useEffect(() => {
    if (editingPlan) {
      setFormErrors({});
    }
  }, [editingPlan]);

  // Fetch inventory if not available (only once on mount)
  useEffect(() => {
    if (!inventory || inventory.length === 0) {
      getInventory();
    }
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    if (!products || products.length === 0) {
      getProducts();
    }
  }, []); // Load products for smart deal configuration

  // Fetch plans when inventory becomes available (only once)
  useEffect(() => {
    if (inventory && inventory.length > 0 && !plansFetchedRef.current) {
      plansFetchedRef.current = true;
      fetchPlans();
    }
  }, [inventory?.length]); // Only depend on inventory length

  useEffect(() => {
    fetchBenefits();
  }, [fetchBenefits]);

  useEffect(() => {
    if (!categories || categories.length === 0) {
      getCategories();
    }
  }, [categories?.length, getCategories]);

  // Filter inventory based on user role and hub assignments (like product form)
  useEffect(() => {
    // Build inventory options: show all, but disable unassigned for restricted roles
    const filtered = (inventory || [])
      .filter(inv => inv && (inv.id || inv.value)) // Ensure valid data
      .map((inv) => {
        const hubId = inv.id || inv.value;
        const displayName = inv.name || inv.place || `Hub ${hubId}`;

        return {
          ...inv,
          value: hubId,  // Ensure value field exists
          name: displayName,
          disabled: false // Allow selection of all hubs for subscription configuration
        };
      });

    setFilteredInventory(filtered);
  }, [inventory]);

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      const customBenefits = plan.custom_benefits && typeof plan.custom_benefits === 'object'
        ? plan.custom_benefits
        : {};
      const exclusiveConfig = customBenefits.EXCLUSIVE_OFFERS || {};
      const additionalBenefits = Object.entries(customBenefits)
        .map(([code, config]) => ({
          code,
          config_text: JSON.stringify(config || {}, null, 2)
        }));
      const smartDealIds = Array.isArray(plan.smart_deal_product_ids)
        ? plan.smart_deal_product_ids
        : (() => {
            if (typeof plan.smart_deal_product_ids === 'string') {
              try {
                const parsed = JSON.parse(plan.smart_deal_product_ids);
                return Array.isArray(parsed) ? parsed : [];
              } catch (error) {
                return [];
              }
            }
            return [];
          })();

      setEditingPlan(plan);
      setFormData({
        ...createDefaultFormData(),
        plan_name: plan.plan_name || '',
        plan_type: plan.plan_type || 'premium',
        description: plan.description || '',
        duration_value: plan.duration_value || 12,
        duration_type: plan.duration_type || 'months',
        custom_start_date: formatDateTimeLocalValue(plan.custom_start_date),
        custom_end_date: formatDateTimeLocalValue(plan.custom_end_date),
        base_price: plan.base_price || '',
        currency: plan.currency || 'INR',
        plan_scope: plan.plan_scope || 'specific_hubs',
        applicable_hubs: plan.applicable_hubs_details?.map(h => h.id).filter(id => id != null) || [],
        single_hub: plan.single_hub?.id || plan.single_hub || null,
        show_exclusive_offers_only: Boolean(exclusiveConfig.exclusive_only ?? plan.show_exclusive_offers_only),
        mapped_offers: Array.isArray(exclusiveConfig.offer_ids)
          ? exclusiveConfig.offer_ids.filter(id => id != null)
          : (Array.isArray(plan.mapped_offers) ? plan.mapped_offers.filter(id => id != null) : []),
        additional_benefits: additionalBenefits,
        loyalty_multiplier: plan.loyalty_multiplier != null ? String(plan.loyalty_multiplier) : '2.00',
        loyalty_once_percentage: plan.loyalty_once_percentage != null ? String(plan.loyalty_once_percentage) : '0.50',
        random_offer_trigger_orders: plan.random_offer_trigger_orders || 0,
        random_offer_percentage: plan.random_offer_percentage != null ? String(plan.random_offer_percentage) : '0.00',
        smart_deal_product_ids: smartDealIds.filter(id => id != null),
        smart_deal_non_subscribed_product_ids: Array.isArray(plan.smart_deal_non_subscribed_product_ids)
          ? plan.smart_deal_non_subscribed_product_ids.filter(id => id != null)
          : (() => {
              if (typeof plan.smart_deal_non_subscribed_product_ids === 'string') {
                try {
                  const parsed = JSON.parse(plan.smart_deal_non_subscribed_product_ids);
                  return Array.isArray(parsed) ? parsed : [];
                } catch (error) {
                  return [];
                }
              }
              return [];
            })(),
        smart_deal_non_subscribed_limit: plan.smart_deal_non_subscribed_limit || 0,
        smart_deal_show_to_non_subscribers: Boolean(plan.smart_deal_show_to_non_subscribers),
        is_active: plan.is_active !== undefined ? plan.is_active : true,
        sort_order: plan.sort_order || 0
      });
    } else {
      setEditingPlan(null);
      setFormData(createDefaultFormData());
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingPlan(null);
  };

  const handleOpenBenefitDialog = (benefit = null) => {
    if (benefit) {
      setEditingBenefit(benefit);
      setBenefitForm({
        code: benefit.code || '',
        name: benefit.name || '',
        description: benefit.description || '',
        config_mode:
          Array.isArray(benefit.default_config?.offer_ids) && benefit.default_config.offer_ids.length > 0
            ? 'offers'
            : benefit.default_config?.multiplier !== undefined
              ? 'multiplier'
              : (
                  benefit.default_config?.percentage !== undefined ||
                  benefit.default_config?.discount_percentage !== undefined
                )
                ? 'percentage'
                : 'none',
        config_value:
          benefit.default_config?.multiplier ??
          benefit.default_config?.percentage ??
          benefit.default_config?.discount_percentage ??
          '',
        default_offer_ids: Array.isArray(benefit.default_config?.offer_ids)
          ? benefit.default_config.offer_ids
          : [],
        is_active: benefit.is_active !== false
      });
    } else {
      setEditingBenefit(null);
      setBenefitForm({
        code: '',
        name: '',
        description: '',
        config_mode: 'none',
        config_value: '',
        default_offer_ids: [],
        is_active: true
      });
    }
    setBenefitFormErrors({});
    setBenefitDialogOpen(true);
  };

  const handleCloseBenefitDialog = () => {
    setBenefitDialogOpen(false);
    setEditingBenefit(null);
  };

  const validateBenefitForm = () => {
    const errors = {};
    const normalizedCode = String(benefitForm.code || '').trim().toUpperCase().replace(/\s+/g, '_');

    if (!normalizedCode) {
      errors.code = 'Benefit code is required';
    }

    if (!benefitForm.name.trim()) {
      errors.name = 'Benefit name is required';
    }

    if (['percentage', 'multiplier'].includes(benefitForm.config_mode)) {
      const numericValue = Number(benefitForm.config_value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        errors.config_value = 'Enter a valid positive number';
      }
      if (benefitForm.config_mode === 'percentage' && numericValue > 100 && normalizedCode === 'FREE_DELIVERY') {
        errors.config_value = 'Delivery percentage cannot exceed 100';
      }
    }

    if (benefitForm.config_mode === 'free_delivery' && normalizedCode !== 'FREE_DELIVERY') {
      errors.code = 'Use benefit code FREE_DELIVERY for Free Delivery config';
    }

    setBenefitFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveBenefit = async () => {
    if (!validateBenefitForm()) {
      return;
    }

    const normalizedCode = String(benefitForm.code || '').trim().toUpperCase().replace(/\s+/g, '_');
    const defaultConfig = {};

    if (benefitForm.config_mode === 'multiplier' && benefitForm.config_value !== '') {
      defaultConfig.multiplier = Number(benefitForm.config_value);
    }

    if (benefitForm.config_mode === 'percentage' && benefitForm.config_value !== '') {
      if (normalizedCode === 'FREE_DELIVERY') {
        defaultConfig.discount_percentage = Number(benefitForm.config_value);
      } else {
        defaultConfig.percentage = Number(benefitForm.config_value);
      }
    }

    if (benefitForm.config_mode === 'free_delivery') {
      defaultConfig.free_delivery = true;
    }

    if (
      (benefitForm.config_mode === 'offers' || normalizedCode === 'EXCLUSIVE_OFFERS') &&
      Array.isArray(benefitForm.default_offer_ids) &&
      benefitForm.default_offer_ids.length > 0
    ) {
      defaultConfig.offer_ids = benefitForm.default_offer_ids;
    }

    const payload = {
      code: normalizedCode,
      name: benefitForm.name.trim(),
      description: benefitForm.description,
      default_config: defaultConfig,
      is_active: benefitForm.is_active
    };

    try {
      if (editingBenefit) {
        await axios.put(`subscriptions/benefits/${editingBenefit.id}/`, payload);
        showSnackbar('Benefit updated successfully');
      } else {
        await axios.post('subscriptions/benefits/', payload);
        showSnackbar('Benefit created successfully');
      }

      handleCloseBenefitDialog();
      fetchBenefits();
    } catch (error) {
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to save benefit';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDeleteBenefit = async (benefit) => {
    if (!window.confirm(`Delete benefit "${benefit.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`subscriptions/benefits/${benefit.id}/`);
      showSnackbar('Benefit deleted successfully');
      setBenefits(currentBenefits => currentBenefits.filter(item => item.id !== benefit.id));
      setFormData(currentData => ({
        ...currentData,
        additional_benefits: currentData.additional_benefits.filter(item => item.code !== benefit.code)
      }));
    } catch (error) {
      showSnackbar('Failed to delete benefit', 'error');
    }
  };

  const validateForm = () => {
    const errors = {};
    const selectableBenefitCodes = new Set(
      benefits.map(benefit => String(benefit.code || '').trim().toUpperCase())
    );
    const selectedBenefitCodes = new Set(
      formData.additional_benefits
        .map(benefit => String(benefit.code || '').trim().toUpperCase())
        .filter(Boolean)
    );

    // Required field validations
    if (!formData.plan_name.trim()) {
      errors.plan_name = 'Plan name is required';
    } else if (formData.plan_name.length < 3) {
      errors.plan_name = 'Plan name must be at least 3 characters';
    }

    if (!formData.base_price || formData.base_price <= 0) {
      errors.base_price = 'Valid price is required';
    }

    if (formData.duration_type === 'custom') {
      if (!formData.custom_start_date) {
        errors.custom_start_date = 'Custom start date is required';
      }
      if (!formData.custom_end_date) {
        errors.custom_end_date = 'Custom end date is required';
      }
      if (formData.custom_start_date && formData.custom_end_date) {
        const start = new Date(formData.custom_start_date);
        const end = new Date(formData.custom_end_date);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          errors.custom_end_date = 'Enter valid custom start and end date';
        } else if (end <= start) {
          errors.custom_end_date = 'Custom end date must be after start date';
        } else if (isCustomWindowPast(formData)) {
          /*
           * A window that has already closed can still be saved, and the plan
           * then sits on the shelf selling a subscription that expires the
           * moment it is bought. That has happened. The form cannot fix the
           * ones already saved, but it can refuse to make another.
           */
          errors.custom_end_date = 'This window has already ended';
        }
      }
    } else {
      const value = Number(formData.duration_value);
      if (!Number.isFinite(value) || value <= 0) {
        errors.duration_value = 'Valid duration is required';
      } else if (!Number.isInteger(value)) {
        // The backend multiplies whole units; half a month is not a thing it
        // can express, and it would silently truncate.
        errors.duration_value = 'Duration must be a whole number';
      }
    }

    // Hub validations based on plan scope
    if (inventory.length === 0) {
      if (formData.plan_scope === 'all_hubs') {
        errors.applicable_hubs = 'No hubs available. Please create inventory/hubs first.';
      } else if (formData.plan_scope === 'single_hub') {
        errors.single_hub = 'No hubs available. Please create inventory/hubs first.';
      } else {
        errors.applicable_hubs = 'No hubs available. Please create inventory/hubs first.';
      }
    } else {
      if (formData.plan_scope === 'specific_hubs' && formData.applicable_hubs.length === 0) {
        errors.applicable_hubs = 'Please select at least one hub';
      } else if (formData.plan_scope === 'single_hub' && !formData.single_hub) {
        errors.single_hub = 'Please select a hub';
      }
    }

    // Exclusive offers validation
    if (selectedBenefitCodes.has('EXCLUSIVE_OFFERS') && formData.show_exclusive_offers_only && formData.mapped_offers.length === 0) {
      // Only show error for new plans, not when editing existing plans
      // (existing plans should already have valid data)
      if (!editingPlan) {
        errors.mapped_offers = 'Please map at least one offer for exclusive plans';
      }
    }

    const seenCodes = new Set();
    formData.additional_benefits.forEach((benefit, index) => {
      const normalizedCode = String(benefit.code || '').trim().toUpperCase();
      if (!normalizedCode) {
        errors[`additional_benefit_${index}`] = 'Benefit code is required';
        return;
      }

      if (seenCodes.has(normalizedCode)) {
        errors[`additional_benefit_${index}`] = 'Benefit code must be unique';
        return;
      }

      if (!selectableBenefitCodes.has(normalizedCode)) {
        errors[`additional_benefit_${index}`] = 'Please select a valid created benefit';
        return;
      }

      seenCodes.add(normalizedCode);

      if (benefit.config_text && benefit.config_text.trim()) {
        try {
          JSON.parse(benefit.config_text);
        } catch (jsonError) {
          errors[`additional_benefit_${index}`] = 'Benefit config must be valid JSON';
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar('Please fix the validation errors', 'error');
      return;
    }

    setSavingPlan(true);
    try {
      const customBenefits = {};

      formData.additional_benefits.forEach((benefit) => {
        const normalizedCode = String(benefit.code || '').trim().toUpperCase();
        if (!normalizedCode) {
          return;
        }

        let parsedConfig = {};
        if (benefit.config_text && String(benefit.config_text).trim()) {
          try {
            const parsed = JSON.parse(benefit.config_text);
            parsedConfig = parsed && typeof parsed === 'object' ? parsed : {};
          } catch (parseErr) {
            throw new Error(`Invalid JSON config for benefit ${normalizedCode}`);
          }
        }

        const normalizedConfig = { ...parsedConfig };

        if (normalizedCode === 'EXCLUSIVE_OFFERS') {
          normalizedConfig.exclusive_only = Boolean(formData.show_exclusive_offers_only);
          normalizedConfig.offer_ids = formData.show_exclusive_offers_only
            ? (formData.mapped_offers || []).filter(id => id != null)
            : [];
        }

        customBenefits[normalizedCode] = normalizedConfig;
      });

      const submitData = {
        plan_name: formData.plan_name,
        plan_type: formData.plan_type,
        description: formData.description,
        duration_value: formData.duration_type === 'custom' ? Number(formData.duration_value || 1) : Number(formData.duration_value),
        duration_type: formData.duration_type,
        custom_start_date: formData.duration_type === 'custom' ? formData.custom_start_date : null,
        custom_end_date: formData.duration_type === 'custom' ? formData.custom_end_date : null,
        base_price: formData.base_price,
        currency: formData.currency,
        plan_scope: formData.plan_scope,
        custom_benefits: customBenefits,
        loyalty_multiplier: formData.loyalty_multiplier !== '' ? Number(formData.loyalty_multiplier) : 0,
        loyalty_once_percentage: formData.loyalty_once_percentage !== '' ? Number(formData.loyalty_once_percentage) : 0,
        random_offer_trigger_orders: Number(formData.random_offer_trigger_orders) || 0,
        random_offer_percentage: formData.random_offer_percentage !== '' ? Number(formData.random_offer_percentage) : 0,
        smart_deal_product_ids: Array.isArray(formData.smart_deal_product_ids)
          ? formData.smart_deal_product_ids.filter(id => id != null)
          : [],
        smart_deal_non_subscribed_product_ids: Array.isArray(formData.smart_deal_non_subscribed_product_ids)
          ? formData.smart_deal_non_subscribed_product_ids.filter(id => id != null)
          : [],
        smart_deal_non_subscribed_limit: Number(formData.smart_deal_non_subscribed_limit) || 0,
        smart_deal_show_to_non_subscribers: Boolean(formData.smart_deal_show_to_non_subscribers),
        is_active: formData.is_active,
        sort_order: Number(formData.sort_order) || 0,
        // Remove fields that shouldn't be sent based on plan_scope
        applicable_hubs: formData.plan_scope === 'specific_hubs' ?
          (formData.applicable_hubs || []).filter(id => id != null) : [],
        single_hub: formData.plan_scope === 'single_hub' ? formData.single_hub : null
      };

      if (editingPlan) {
        await axios.put(`subscriptions/plans/${editingPlan.id}/`, submitData);
        showSnackbar('Subscription plan updated successfully');
      } else {
        await axios.post('subscriptions/plans/', submitData);
        showSnackbar('Subscription plan created successfully');
      }

      // The plan saved fine — but a selection outside a 2-for-1 category will
      // never reach a customer, and silently succeeding is how three of Gold's
      // four smart deals ended up invisible with nothing on screen to say so.
      const warnings = [
        orphanWarning(submitData.smart_deal_product_ids, 'Subscriber smart deals'),
        orphanWarning(
          submitData.smart_deal_non_subscribed_product_ids,
          'Non-subscriber smart deals'
        ),
      ].filter(Boolean);
      if (warnings.length) {
        showSnackbar(warnings.join(' '), 'warning');
      }

      handleCloseDialog();
      fetchPlans();
    } catch (error) {
      const data = error.response?.data;
      let errorMessage = 'Failed to save subscription plan';
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data?.detail) {
        errorMessage = data.detail;
      } else if (data?.message) {
        errorMessage = data.message;
      } else if (data && typeof data === 'object') {
        const firstKey = Object.keys(data)[0];
        const firstVal = data[firstKey];
        errorMessage = `${firstKey}: ${Array.isArray(firstVal) ? firstVal[0] : JSON.stringify(firstVal)}`;
      }
      showSnackbar(errorMessage, 'error');
      console.error('Subscription plan save failed', data || error);
    } finally {
      setSavingPlan(false);
    }
  };

  const handleOpenDeleteDialog = (plan) => {
    setDeleteDialog({ open: true, plan });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, plan: null });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.plan) return;

    try {
      await axios.delete(`subscriptions/plans/${deleteDialog.plan.id}/`);
      showSnackbar('Subscription plan deleted successfully');
      fetchPlans();
    } catch (error) {
      showSnackbar('Failed to delete subscription plan', 'error');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleToggleActive = async (plan) => {
    const next = !plan.is_active;
    await statusToggle.run(plan.id, next, async () => {
      try {
        await axios.patch(`subscriptions/plans/${plan.id}/`, {
          is_active: next
        });
      } catch (error) {
        showSnackbar('Failed to update plan status', 'error');
        throw error;
      }
      showSnackbar(`Plan ${next ? 'activated' : 'deactivated'} successfully`);
      await fetchPlans();
    });
  };

  const formatBenefits = (plan) => {
    if (plan.benefits_summary) {
      return plan.benefits_summary;
    }

    const benefitCodes = Object.keys(plan.custom_benefits || {});
    if (benefitCodes.length > 0) {
      return benefitCodes.map(formatBenefitLabel).join(', ');
    }

    const benefits = [];
    if (plan.free_delivery) benefits.push('Free Delivery');
    if (plan.double_loyalty_points) benefits.push('2x Loyalty Points');
    if (plan.exclusive_offers) benefits.push('Exclusive Offers');
    return benefits.join(', ') || 'No benefits';
  };

  const formatBenefitDefaultConfig = (benefit) => {
    const config = benefit?.default_config && typeof benefit.default_config === 'object'
      ? benefit.default_config
      : {};

    if (config.multiplier !== undefined) {
      return `Default: ${config.multiplier}x multiplier`;
    }

    if (config.discount_percentage !== undefined) {
      return `Default: ${config.discount_percentage}% delivery discount`;
    }

    if (config.percentage !== undefined) {
      return `Default: ${config.percentage}% value`;
    }

    if (Array.isArray(config.offer_ids) && config.offer_ids.length > 0) {
      return `Default offers: ${config.offer_ids.length}`;
    }

    return 'No default config';
  };

  const selectedBenefitCodeSet = new Set(
    formData.additional_benefits
      .map((mapped) => String(mapped.code || '').trim().toUpperCase())
      .filter(Boolean)
  );
  const getSelectableBenefitsForRow = (rowIndex) =>
    benefits.filter((item) => {
      const code = String(item.code || '').trim().toUpperCase();
      if (!code) return false;
      const currentCode = String(formData.additional_benefits[rowIndex]?.code || '')
        .trim()
        .toUpperCase();
      if (code === currentCode) return true;
      return !selectedBenefitCodeSet.has(code);
    });
  const canAddMoreBenefits = benefits.some((item) => {
    const code = String(item.code || '').trim().toUpperCase();
    return code && !selectedBenefitCodeSet.has(code);
  });
  const hasExclusiveOffersMapping = formData.additional_benefits.some(
    benefit => String(benefit.code || '').trim().toUpperCase() === 'EXCLUSIVE_OFFERS'
  );
  const showLegacyBenefitConfig =
    Boolean(editingBenefit) &&
    (benefitForm.config_mode === 'percentage' || benefitForm.config_mode === 'multiplier');
  const selectedHubIds = formData.plan_scope === 'single_hub'
    ? (formData.single_hub ? [Number(formData.single_hub)] : [])
    : formData.plan_scope === 'specific_hubs'
      ? (formData.applicable_hubs || []).map(id => Number(id))
      : [];
  const scopedOffers = offers.filter((offer) => {
    const offerHubIds = Array.isArray(offer.inventory_list) ? offer.inventory_list.map(id => Number(id)) : [];
    if (selectedHubIds.length === 0 || offerHubIds.length === 0) {
      return true;
    }
    return offerHubIds.some(id => selectedHubIds.includes(id));
  });
  const mappedCategories = categories.filter((category) => {
    const hubList = Array.isArray(category.invetory_list)
      ? category.invetory_list
      : Array.isArray(category.inventory_list)
        ? category.inventory_list
        : [];
    if (formData.plan_scope === 'all_hubs') {
      return true;
    }
    if (selectedHubIds.length === 0) {
      return false;
    }
    return hubList.some(id => selectedHubIds.includes(Number(id)));
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Subscription Plans Management
        </Typography>
        <Box display="flex" gap={1}>
          <GrowthSecondaryButton
            startIcon={<Add />}
            onClick={() => handleOpenBenefitDialog()}
          >
            Add Benefit
          </GrowthSecondaryButton>
          <GrowthPrimaryButton
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add New Plan
          </GrowthPrimaryButton>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Benefit Master</Typography>
            <Button variant="text" onClick={fetchBenefits}>Refresh Benefits</Button>
          </Box>
          {benefits.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No benefits created yet. Create benefits here, then map them in subscription plans.
            </Typography>
          ) : (
            <Grid container spacing={1}>
              {benefits.map((benefit) => (
                <Grid item xs={12} md={6} key={benefit.id}>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {benefit.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {benefit.code}
                      </Typography>
                      <Typography display="block" variant="caption" color="text.secondary">
                        {formatBenefitDefaultConfig(benefit)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        size="small"
                        label={benefit.is_active ? 'Active' : 'Inactive'}
                        color={benefit.is_active ? 'success' : 'default'}
                      />
                      <IconButton size="small" color="primary" onClick={() => handleOpenBenefitDialog(benefit)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteBenefit(benefit)}>
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : plans.length === 0 ? (
        <Box textAlign="center" p={6}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Subscription Plans Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Create your first subscription plan to get started with premium memberships.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ backgroundColor: '#ED1E79', '&:hover': { backgroundColor: '#d0176a' } }}
          >
            Create First Plan
          </Button>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Plan Name</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Price</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
                <TableCell><strong>Benefits</strong></TableCell>
                <TableCell><strong>Scope</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {plan.plan_name}
                    </Typography>
                    {plan.description && (
                      <Typography variant="caption" color="text.secondary">
                        {plan.description.length > 50 ? `${plan.description.substring(0, 50)}...` : plan.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={plan.plan_type}
                      size="small"
                      color={plan.plan_type === 'premium' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="medium">
                      ₹{plan.base_price}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {/*
                      Times included. This printed toLocaleDateString(), so a
                      window running 16:16 to 16:19 read as "25/08/2026 -
                      25/08/2026" -- indistinguishable from one that ran all
                      day, which is exactly how it was read. And "1 months" is
                      now "1 month".
                    */}
                    {planDurationSummary(plan)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatBenefits(plan)}>
                      <Typography variant="body2" sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatBenefits(plan)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        plan.plan_scope === 'all_hubs' ? 'All Hubs' :
                        plan.plan_scope === 'specific_hubs' ? 'Specific Hubs' :
                        plan.plan_scope === 'single_hub' ? 'Single Hub' :
                        plan.plan_scope.replace('_', ' ')
                      }
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <StatusSwitch
                      checked={statusToggle.displayed(plan.id, plan.is_active)}
                      pending={statusToggle.isPending(plan.id)}
                      onChange={() => handleToggleActive(plan)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(plan)}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(plan)}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: growthDialogPaperSx }}
      >
        <DialogTitle sx={growthDialogTitleSx}>
          {editingPlan ? 'Edit Subscription Plan' : 'Add New Subscription Plan'}
        </DialogTitle>
        <DialogContent sx={{ fontFamily: GROWTH_FONT }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plan Name"
                value={formData.plan_name}
                onChange={(e) => setFormData({...formData, plan_name: e.target.value})}
                error={!!formErrors.plan_name}
                helperText={formErrors.plan_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Plan Type</InputLabel>
                <Select
                  value={formData.plan_type}
                  onChange={(e) => setFormData({...formData, plan_type: e.target.value})}
                  label="Plan Type"
                >
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="basic">Basic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Plan Scope</InputLabel>
                <Select
                  value={formData.plan_scope}
                  onChange={(e) => {
                    const newScope = e.target.value;
                    setFormData({
                      ...formData,
                      plan_scope: newScope,
                      // Clear selections when changing scope
                      applicable_hubs: newScope === 'all_hubs' ? [] : formData.applicable_hubs,
                      single_hub: newScope === 'single_hub' ? null : formData.single_hub
                    });
                  }}
                  label="Plan Scope"
                >
                  <MenuItem value="all_hubs">All Hubs in Franchise</MenuItem>
                  <MenuItem value="specific_hubs">Specific Hubs Only</MenuItem>
                  <MenuItem value="single_hub">Single Hub Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={formData.base_price}
                onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                error={!!formErrors.base_price}
                helperText={formErrors.base_price}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Duration Value"
                type="number"
                value={formData.duration_value}
                onChange={(e) => setFormData({...formData, duration_value: e.target.value})}
                error={!!formErrors.duration_value}
                helperText={formErrors.duration_value}
                required={formData.duration_type !== 'custom'}
                disabled={formData.duration_type === 'custom'}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Duration Type</InputLabel>
                <Select
                  value={formData.duration_type}
                  onChange={(e) => setFormData({
                    ...formData,
                    duration_type: e.target.value,
                    duration_value: e.target.value === 'custom' ? 1 : formData.duration_value,
                    custom_start_date: e.target.value === 'custom' ? formData.custom_start_date : '',
                    custom_end_date: e.target.value === 'custom' ? formData.custom_end_date : ''
                  })}
                  label="Duration Type"
                >
                  {/*
                    The backend has accepted minutes, hours and days for a
                    while; this list offered only three of the six, so the short
                    plans could not be created at all. Grouped rather than flat:
                    six loose options make a ten-minute plan look like an equal
                    sibling of a yearly one.
                  */}
                  {DURATION_UNITS.map((unit, index) => {
                    const startsGroup =
                      index === 0 || DURATION_UNITS[index - 1].group !== unit.group;
                    return startsGroup
                      ? [
                          <ListSubheader key={`${unit.group}-head`}>
                            {unit.group}
                          </ListSubheader>,
                          <MenuItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </MenuItem>,
                        ]
                      : (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </MenuItem>
                      );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {/*
              What the plan will actually do.

              The form asked for a number and a unit and then said nothing. For
              months that is survivable; for a ten-minute plan it is the
              difference between a working plan and one that has already expired
              by the time the form is submitted -- which is what happened. The
              arithmetic is the backend's own, so this cannot promise an expiry
              the server will not honour.
            */}
            {formData.duration_type !== 'custom' && durationPreview && (
              <Grid item xs={12}>
                <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                  Members get <strong>{durationPreview.label}</strong>.
                  {durationPreview.expiry
                    ? <> Someone joining now would expire at <strong>{durationPreview.expiry}</strong>.</>
                    : null}
                </Alert>
              </Grid>
            )}
            {formData.duration_type === 'custom' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Custom Start Date"
                    type="datetime-local"
                    value={formData.custom_start_date}
                    onChange={(e) => setFormData({...formData, custom_start_date: e.target.value})}
                    error={!!formErrors.custom_start_date}
                    helperText={formErrors.custom_start_date}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Custom End Date"
                    type="datetime-local"
                    value={formData.custom_end_date}
                    onChange={(e) => setFormData({...formData, custom_end_date: e.target.value})}
                    error={!!formErrors.custom_end_date}
                    helperText={formErrors.custom_end_date}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "14PX", fontWeight: "600", lineHeight: "22px" }}>
                Loyalty & Offer Configuration
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Loyalty Multiplier (Active)"
                type="number"
                value={formData.loyalty_multiplier}
                onChange={(e) => setFormData({ ...formData, loyalty_multiplier: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Applied to loyalty points for active subscribers"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Loyalty % for Past Subscribers"
                type="number"
                value={formData.loyalty_once_percentage}
                onChange={(e) => setFormData({ ...formData, loyalty_once_percentage: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Applied when user purchased a plan at least once and is not currently active (order × %)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Random Offer Trigger Orders"
                type="number"
                value={formData.random_offer_trigger_orders}
                onChange={(e) => setFormData({ ...formData, random_offer_trigger_orders: e.target.value })}
                inputProps={{ min: 0, step: 1 }}
                helperText="0 = off. Every Nth paid order while active triggers the % off (cart, before pay)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Random Offer Percentage"
                type="number"
                value={formData.random_offer_percentage}
                onChange={(e) => setFormData({ ...formData, random_offer_percentage: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="% off subtotal on the trigger order (shown in cart before pay)"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={optionsIncludingSaved(formData.smart_deal_product_ids)}
                getOptionLabel={(option) => option?._smartDealLabel || option?.name || option?.item_name || `Product ${option?.value || option?.id}`}
                value={optionsIncludingSaved(formData.smart_deal_product_ids).filter(product => formData.smart_deal_product_ids.includes(product.value || product.id))}
                onChange={(event, newValue) => {
                  const selectedIds = newValue.map(item => item.value || item.id).filter(id => id != null);
                  setFormData({ ...formData, smart_deal_product_ids: selectedIds });
                  const missingBatch = newValue.filter((p) => p?._hasSmartDealStock === false);
                  if (missingBatch.length) {
                    showSnackbar(
                      `${missingBatch.length} selected product(s) are out of stock — add a valid hub batch so they appear on mobile.`,
                      'warning'
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="2-for-1 Smart Deal Products (Subscribers)"
                    placeholder="Select smart deal products"
                    helperText="Only these products appear in 2-for-1 for active subscribers. Products need hub inventory (available units > 0) to show on mobile."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={optionsIncludingSaved(formData.smart_deal_non_subscribed_product_ids)}
                getOptionLabel={(option) => option?._smartDealLabel || option?.name || option?.item_name || `Product ${option?.value || option?.id}`}
                value={optionsIncludingSaved(formData.smart_deal_non_subscribed_product_ids).filter(product =>
                  formData.smart_deal_non_subscribed_product_ids.includes(product.value || product.id)
                )}
                onChange={(event, newValue) => {
                  const selectedIds = newValue.map(item => item.value || item.id).filter(id => id != null);
                  setFormData({ ...formData, smart_deal_non_subscribed_product_ids: selectedIds });
                  const missingBatch = newValue.filter((p) => p?._hasSmartDealStock === false);
                  if (missingBatch.length) {
                    showSnackbar(
                      `${missingBatch.length} selected product(s) are out of stock — add a valid hub batch so they appear on mobile.`,
                      'warning'
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Smart Deal Products (Non-Subscribed)"
                    placeholder="Select products for non‑subscribed users"
                    helperText="If empty, the 2-for-1 category is hidden for non-subscribers. Otherwise only these in-stock products show."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Smart Deal Limit (Non-Subscribed)"
                type="number"
                value={formData.smart_deal_non_subscribed_limit}
                onChange={(e) => setFormData({ ...formData, smart_deal_non_subscribed_limit: e.target.value })}
                inputProps={{ min: 0, step: 1 }}
                helperText="0 shows all configured non-subscriber smart deals"
              />
            </Grid>
            <Grid item xs={12}>
              {/*
                Without this, a product mapped only to the subscriber list is
                invisible to everyone who has not subscribed: the app removes
                2-for-1 products from their own category, and a non-subscriber's
                2-for-1 category held only the preview list. It looked exactly
                like the product failing to appear.
              */}
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.smart_deal_show_to_non_subscribers)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        smart_deal_show_to_non_subscribers: e.target.checked,
                      })
                    }
                  />
                }
                label="Show the full 2-for-1 menu to non-subscribers"
              />
              <Typography sx={{ fontSize: "12px", color: "text.secondary", ml: "48px", mt: "-6px" }}>
                {formData.smart_deal_show_to_non_subscribers
                  ? "Everyone sees every product mapped to this plan. Only subscribers can claim the 2-for-1 price."
                  : "Non-subscribers see only the preview products above. Everything else is hidden from them app-wide."}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>
                  {formData.plan_scope === 'all_hubs' ? 'Plan Scope' :
                   formData.plan_scope === 'single_hub' ? 'Applicable Hub' : 'Applicable Hubs'}
                </Typography>

                {formData.plan_scope === 'all_hubs' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        This plan will be available at all hubs in the franchise.
                      </Typography>
                    </Box>
                    {filteredInventory.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {filteredInventory.map((hub) => (
                          <Chip
                            key={hub.value}
                            label={hub.name}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hubs loaded yet. Refresh inventory to see the hub list.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Autocomplete
                    multiple={formData.plan_scope !== 'single_hub'}
                    options={filteredInventory}
                    getOptionLabel={(option) => option.name}
                    value={
                      formData.plan_scope === 'single_hub' ?
                        (filteredInventory.find(hub => hub.value === formData.single_hub) || null) :
                        filteredInventory.filter(hub => formData.applicable_hubs.includes(hub.value))
                    }
                    onChange={(event, newValue) => {
                      if (formData.plan_scope === 'single_hub') {
                        // For single hub, newValue is a single object or null
                        const selectedId = newValue ? newValue.value : null;
                        setFormData(prevData => ({
                          ...prevData,
                          single_hub: selectedId,
                          applicable_hubs: [] // Clear applicable_hubs for single hub
                        }));
                      } else {
                        // For specific hubs, newValue is an array of objects
                        const selectedIds = newValue.map(hub => hub.value);
                        setFormData(prevData => ({
                          ...prevData,
                          applicable_hubs: selectedIds,
                          single_hub: null // Clear single_hub for specific hubs
                        }));
                      }
                    }}
                    disabled={!filteredInventory || filteredInventory.length === 0}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={formData.plan_scope === 'single_hub' ? "Select Single Hub" : "Select Applicable Hubs"}
                        placeholder={
                          formData.plan_scope === 'single_hub' ?
                            "Choose one hub" :
                            "Choose multiple hubs"
                        }
                        error={!!formErrors.applicable_hubs}
                        helperText={formErrors.applicable_hubs}
                      />
                    )}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          checked={selected}
                          style={{ marginRight: 8 }}
                        />
                        {option.name}
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    limitTags={formData.plan_scope === 'single_hub' ? 1 : 3}
                  />
                )}

                {formErrors.applicable_hubs && (
                  <Typography style={{ color: "red", fontSize: "12PX", fontWeight: "400", lineHeight: "22px" }}>
                    {formErrors.applicable_hubs}
                  </Typography>
                )}
                {formErrors.single_hub && (
                  <Typography style={{ color: "red", fontSize: "12PX", fontWeight: "400", lineHeight: "22px" }}>
                    {formErrors.single_hub}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>
                  Mapped Categories
                </Typography>
                {mappedCategories.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {formData.plan_scope === 'all_hubs'
                      ? 'No categories available.'
                      : 'No categories mapped to the selected hub(s).'}
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {mappedCategories.map((category) => (
                      <Chip
                        key={category.id || category.category_name}
                        label={category.category_name}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Benefit Mappings
              </Typography>
              {formData.additional_benefits.map((benefit, index) => (
                <Grid container spacing={2} key={`${benefit.code || 'benefit'}-${index}`} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth error={!!formErrors[`additional_benefit_${index}`]}>
                      <InputLabel>Benefit</InputLabel>
                      <Select
                        value={benefit.code}
                        label="Benefit"
                        onChange={(e) => {
                          const selectedBenefit = benefits.find(item => item.code === e.target.value);
                          const defaultConfig = selectedBenefit?.default_config && typeof selectedBenefit.default_config === 'object'
                            ? selectedBenefit.default_config
                            : {};
                          const nextBenefits = [...formData.additional_benefits];
                          nextBenefits[index] = {
                            ...nextBenefits[index],
                            code: e.target.value,
                            config_text: JSON.stringify(defaultConfig)
                          };
                          setFormData({
                            ...formData,
                            additional_benefits: nextBenefits,
                            show_exclusive_offers_only: e.target.value === 'EXCLUSIVE_OFFERS'
                              ? Boolean(defaultConfig.exclusive_only)
                              : formData.show_exclusive_offers_only,
                            mapped_offers: e.target.value === 'EXCLUSIVE_OFFERS' && Array.isArray(defaultConfig.offer_ids)
                              ? defaultConfig.offer_ids
                              : formData.mapped_offers
                          });
                        }}
                      >
                        {getSelectableBenefitsForRow(index).map((item) => (
                          <MenuItem key={item.id} value={item.code}>
                            {item.name} ({item.code})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {formErrors[`additional_benefit_${index}`] && (
                      <Typography variant="caption" color="error">
                        {formErrors[`additional_benefit_${index}`]}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                      }}
                    >
                      This benefit will be attached to the subscription plan.
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      color="error"
                      variant="outlined"
                      onClick={() => {
                        const nextBenefits = formData.additional_benefits.filter((_, itemIndex) => itemIndex !== index);
                        setFormData({ ...formData, additional_benefits: nextBenefits });
                      }}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}
              <Button
                variant="outlined"
                onClick={() => setFormData({
                  ...formData,
                  additional_benefits: [
                    ...formData.additional_benefits,
                    { code: '', config_text: '{}' }
                  ]
                })}
                disabled={!canAddMoreBenefits && formData.additional_benefits.some((b) => !b.code)}
              >
                Add Benefit Mapping
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Map multiple benefits (Free Delivery, Double Loyalty, Random Offer, Smart Deals, Exclusive…).
                Already-mapped codes are hidden from the dropdown.
              </Typography>
              {benefits.length === 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Create a benefit first, then it will appear in this dropdown.
                </Typography>
              )}
            </Grid>

            {hasExclusiveOffersMapping && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.show_exclusive_offers_only}
                      onChange={(e) => setFormData({...formData, show_exclusive_offers_only: e.target.checked})}
                    />
                  }
                  label="Show only mapped offers to subscribers"
                />
              </Grid>
            )}

            {hasExclusiveOffersMapping && formData.show_exclusive_offers_only && (
              <Grid item xs={12}>
                <FormControl fullWidth error={!!formErrors.mapped_offers}>
                  <InputLabel>Mapped Offers</InputLabel>
                  <Select
                    multiple
                    value={formData.mapped_offers}
                    onChange={(e) => setFormData({...formData, mapped_offers: e.target.value})}
                    label="Mapped Offers"
                    renderValue={(selected) =>
                      selected.map(id => scopedOffers.find(o => o.id === id)?.offer_name || `Offer ${id}`).join(', ')
                    }
                  >
                    {scopedOffers.map((offer) => (
                      <MenuItem key={offer.id} value={offer.id}>
                        {offer.offer_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.mapped_offers && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 1 }}>
                      {formErrors.mapped_offers}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sort Order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={growthDialogActionsSx}>
          <GrowthSecondaryButton onClick={handleCloseDialog} disabled={savingPlan}>
            Cancel
          </GrowthSecondaryButton>
          <GrowthPrimaryButton
            onClick={handleSubmit}
            disabled={savingPlan}
            startIcon={savingPlan ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {savingPlan ? 'Saving…' : (editingPlan ? 'Update' : 'Create')}
          </GrowthPrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: growthDialogPaperSx }}
      >
        <DialogTitle sx={growthDialogTitleSx}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ fontFamily: GROWTH_FONT, pt: 2 }}>
          <Typography sx={{ fontFamily: GROWTH_FONT }}>
            Are you sure you want to delete the subscription plan "{deleteDialog.plan?.plan_name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={growthDialogActionsSx}>
          <GrowthSecondaryButton onClick={handleCloseDeleteDialog}>
            Cancel
          </GrowthSecondaryButton>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            sx={{ fontFamily: GROWTH_FONT, textTransform: 'none', borderRadius: '12px', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={benefitDialogOpen}
        onClose={handleCloseBenefitDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: growthDialogPaperSx }}
      >
        <DialogTitle sx={growthDialogTitleSx}>
          {editingBenefit ? 'Edit Benefit' : 'Create Benefit'}
        </DialogTitle>
        <DialogContent sx={{ fontFamily: GROWTH_FONT }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Benefit Code"
                value={benefitForm.code}
                onChange={(e) => setBenefitForm({
                  ...benefitForm,
                  code: e.target.value.toUpperCase().replace(/\s+/g, '_')
                })}
                error={!!benefitFormErrors.code}
                helperText={benefitFormErrors.code || 'Example: PRIORITY_SUPPORT'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Benefit Name"
                value={benefitForm.name}
                onChange={(e) => setBenefitForm({ ...benefitForm, name: e.target.value })}
                error={!!benefitFormErrors.name}
                helperText={benefitFormErrors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={benefitForm.description}
                onChange={(e) => setBenefitForm({ ...benefitForm, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Config Type</InputLabel>
                <Select
                  value={benefitForm.config_mode}
                  label="Config Type"
                  onChange={(e) => setBenefitForm({
                    ...benefitForm,
                    config_mode: e.target.value,
                    config_value: e.target.value === 'none' || e.target.value === 'offers' ? '' : benefitForm.config_value,
                    default_offer_ids: e.target.value === 'offers' ? benefitForm.default_offer_ids : benefitForm.default_offer_ids,
                    code: e.target.value === 'free_delivery' ? 'FREE_DELIVERY' : benefitForm.code,
                    name: e.target.value === 'free_delivery' ? 'Free Delivery' : benefitForm.name
                  })}
                >
                  <MenuItem value="none">No Default Value</MenuItem>
                  <MenuItem value="free_delivery">Free Delivery</MenuItem>
                  <MenuItem value="offers">Offers</MenuItem>
                  {showLegacyBenefitConfig && (
                    <MenuItem value={benefitForm.config_mode}>
                      {benefitForm.config_mode === 'percentage' ? 'Percentage (legacy)' : 'Multiplier (legacy)'}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            {['percentage', 'multiplier'].includes(benefitForm.config_mode) && showLegacyBenefitConfig && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={benefitForm.config_mode === 'percentage' ? 'Default Percentage' : 'Default Multiplier'}
                  value={benefitForm.config_value}
                  onChange={(e) => setBenefitForm({ ...benefitForm, config_value: e.target.value })}
                  error={!!benefitFormErrors.config_value}
                  helperText={
                    benefitFormErrors.config_value ||
                    (benefitForm.config_mode === 'percentage'
                      ? 'Example: 25'
                      : 'Example: 2')
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            )}
            {(benefitForm.config_mode === 'offers' || String(benefitForm.code || '').trim().toUpperCase() === 'EXCLUSIVE_OFFERS') && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Default Offers</InputLabel>
                  <Select
                    multiple
                    value={benefitForm.default_offer_ids}
                    label="Default Offers"
                    onChange={(e) => setBenefitForm({ ...benefitForm, default_offer_ids: e.target.value })}
                    renderValue={(selected) =>
                      selected.map(id => offers.find(o => o.id === id)?.offer_name || `Offer ${id}`).join(', ')
                    }
                  >
                    {offers.map((offer) => (
                      <MenuItem key={offer.id} value={offer.id}>
                        {offer.offer_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={benefitForm.is_active}
                    onChange={(e) => setBenefitForm({ ...benefitForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={growthDialogActionsSx}>
          <GrowthSecondaryButton onClick={handleCloseBenefitDialog}>Cancel</GrowthSecondaryButton>
          <GrowthPrimaryButton onClick={handleSaveBenefit}>
            {editingBenefit ? 'Update Benefit' : 'Create Benefit'}
          </GrowthPrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Subscription Purchase Dialog */}
      <Dialog
        open={purchaseDialog.open}
        onClose={() => setPurchaseDialog({ open: false, planId: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: growthDialogPaperSx }}
      >
        <DialogTitle sx={growthDialogTitleSx}>
          Subscribe to Plan
        </DialogTitle>
        <DialogContent>
          {purchaseDialog.planId && (
            <SubscriptionPurchase
              planId={purchaseDialog.planId}
              onSuccess={(data) => {
                setPurchaseDialog({ open: false, planId: null });
                showSnackbar('Successfully subscribed to the plan!', 'success');
                // Optionally refresh the plans list
                fetchPlans();
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={growthDialogActionsSx}>
          <GrowthSecondaryButton
            onClick={() => setPurchaseDialog({ open: false, planId: null })}
          >
            Cancel
          </GrowthSecondaryButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
